import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    ShieldAlert,
    AlertTriangle,
    AlertCircle,
    Info,
    ExternalLink,
    Lock,
    Loader2,
    Filter,
    Search,
    RefreshCw,
} from "lucide-react";

import { Breadcrumb } from "../components/Breadcrumb";
import { useAuthStore } from "../state/authStore";
import { usePageMeta } from "../lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
    new: "New",
    acknowledged: "Acknowledged",
    resolved: "Resolved",
    suppressed: "Suppressed",
};

const SEVERITY_STYLES = {
    Critical: {
        bg: "bg-accent-red/15",
        text: "text-accent-red",
        border: "border-accent-red/40",
        Icon: AlertCircle,
    },
    High: {
        bg: "bg-accent-amber/15",
        text: "text-accent-amber",
        border: "border-accent-amber/40",
        Icon: AlertTriangle,
    },
    Medium: {
        bg: "bg-yellow-500/15",
        text: "text-yellow-400",
        border: "border-yellow-500/30",
        Icon: AlertTriangle,
    },
    Low: {
        bg: "bg-zinc-700/40",
        text: "text-text-muted",
        border: "border-white/10",
        Icon: Info,
    },
    None: {
        bg: "bg-zinc-700/40",
        text: "text-text-dim",
        border: "border-white/10",
        Icon: Info,
    },
};

const fmtScore = (v) => (typeof v === "number" ? v.toFixed(1) : "—");

const fmtRelTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return d.toISOString().slice(0, 10);
};

const SeverityBadge = ({ severity }) => {
    const s = SEVERITY_STYLES[severity] || SEVERITY_STYLES.None;
    const Icon = s.Icon;
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-md border ${s.bg} ${s.text} ${s.border} px-1.5 py-0.5 text-[11px] font-mono uppercase tracking-wide`}
            data-testid={`severity-${severity}`}
        >
            <Icon className="w-3 h-3" aria-hidden="true" />
            {severity}
        </span>
    );
};

const StatusPill = ({ status }) => {
    const palette = {
        new: "border-accent-cyan/40 text-accent-cyan",
        acknowledged: "border-yellow-500/30 text-yellow-400",
        resolved: "border-accent-mint/40 text-accent-mint",
        suppressed: "border-white/15 text-text-dim",
    };
    return (
        <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-mono uppercase tracking-wider ${palette[status] || palette.new}`}
            data-testid={`status-${status}`}
        >
            {STATUS_LABELS[status] || status}
        </span>
    );
};

const PaywallCard = () => (
    <div
        className="mx-auto max-w-xl mt-16 rounded-2xl border border-white/[0.08] bg-surface/60 backdrop-blur-md p-8 text-center"
        data-testid="alerts-paywall"
    >
        <Lock className="w-7 h-7 text-accent-cyan mx-auto mb-3" aria-hidden="true" />
        <h2 className="text-xl font-semibold text-text-primary mb-2">
            Continuous CVE scanning is a Pro feature
        </h2>
        <p className="text-text-muted text-sm leading-relaxed mb-5">
            Upgrade to Pro and we'll match every active hardware asset in your
            inventory against the NIST National Vulnerability Database once a
            week — new CVEs land here as soon as they're published.
        </p>
        <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 rounded-md bg-accent-cyan text-canvas px-4 py-2 text-sm font-semibold hover:bg-accent-cyan/90 transition-colors"
            data-testid="alerts-upgrade-cta"
        >
            See pricing
        </Link>
    </div>
);

const HowItWorks = () => (
    <details
        className="rounded-lg border border-white/[0.06] bg-surface/50 p-4 mt-4"
        data-testid="alerts-howitworks"
    >
        <summary className="cursor-pointer text-[12.5px] text-text-muted hover:text-text-primary inline-flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5" /> How CVE scanning works
        </summary>
        <div className="mt-3 text-[13px] text-text-muted leading-relaxed space-y-2">
            <p>
                Every Sunday at 03:00 UTC we walk every active hardware asset in
                your IPAM that has a populated CPE identifier and query it
                against the NIST National Vulnerability Database (NVD 2.0 API).
            </p>
            <p>
                CVEs that match the asset's CPE get an alert here, deduped
                across runs. CVEs that no longer match (e.g. NVD removed them
                after a patch confirmation) are auto-resolved.
            </p>
            <p>
                Severities use CVSS v3.1 base score. References point to the
                vendor advisory when available. No data leaves your tenant —
                only the CPE URI is sent to NVD; the CVE metadata is fetched
                and stored.
            </p>
        </div>
    </details>
);

export default function Alerts() {
    const user = useAuthStore((s) => s.user);
    const isLoading = useAuthStore((s) => s.isLoading);
    const ready = !isLoading;
    usePageMeta({
        title: "CVE Alerts · Cybersecurity Forefront",
        description: "Continuous CVE matching against your hardware inventory.",
        ogType: "website",
    });

    const [statusFilter, setStatusFilter] = useState("new");
    const [severityFilter, setSeverityFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paywalled, setPaywalled] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const load = async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            const params = {
                status: statusFilter,
                severity: severityFilter,
                limit: 100,
            };
            const trimmed = search.trim();
            if (trimmed.toUpperCase().startsWith("CVE-")) {
                params.cve_id = trimmed.toUpperCase();
            }
            const [listRes, statsRes] = await Promise.all([
                axios.get(`${API}/cve-alerts`, {
                    params,
                    withCredentials: true,
                }),
                axios.get(`${API}/cve-alerts/stats`, { withCredentials: true }),
            ]);
            setItems(listRes.data.items || []);
            setStats(statsRes.data);
            setPaywalled(false);
        } catch (err) {
            if (err?.response?.status === 402) {
                setPaywalled(true);
            } else if (err?.response?.status === 401) {
                setErrorMsg("Sign in to see CVE alerts.");
            } else {
                setErrorMsg(
                    err?.response?.data?.detail || "Failed to load alerts.",
                );
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!ready) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, statusFilter, severityFilter]);

    const filteredItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q || q.toUpperCase().startsWith("CVE-")) return items;
        return items.filter((it) => {
            const haystack = [
                it.cve_id,
                it.cve_summary,
                it?.hardware?.vendor,
                it?.hardware?.model,
                it?.hardware?.hostname,
                it?.client?.name,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [items, search]);

    const sevButton = (sev) => {
        const active = severityFilter === sev;
        const s = SEVERITY_STYLES[sev] || SEVERITY_STYLES.None;
        return (
            <button
                key={sev}
                type="button"
                onClick={() =>
                    setSeverityFilter(severityFilter === sev ? "all" : sev)
                }
                className={`px-2 py-1 rounded-md border text-[11.5px] font-mono uppercase tracking-wide transition-colors ${
                    active
                        ? `${s.bg} ${s.text} ${s.border}`
                        : "bg-surface/30 border-white/[0.06] text-text-dim hover:text-text-primary hover:border-white/15"
                }`}
                data-testid={`filter-severity-${sev}`}
            >
                {sev}
                {stats?.by_severity?.[sev] !== undefined && (
                    <span className="ml-1 opacity-60">
                        {stats.by_severity[sev]}
                    </span>
                )}
            </button>
        );
    };

    const statusButton = (st) => {
        const active = statusFilter === st;
        return (
            <button
                key={st}
                type="button"
                onClick={() => setStatusFilter(st)}
                className={`px-2 py-1 rounded-md border text-[11.5px] font-medium transition-colors ${
                    active
                        ? "bg-accent-cyan/15 text-accent-cyan border-accent-cyan/40"
                        : "bg-surface/30 border-white/[0.06] text-text-dim hover:text-text-primary hover:border-white/15"
                }`}
                data-testid={`filter-status-${st}`}
            >
                {STATUS_LABELS[st] || st}
                {stats?.by_status?.[st] !== undefined && st !== "all" && (
                    <span className="ml-1 opacity-60">
                        {stats.by_status[st]}
                    </span>
                )}
            </button>
        );
    };

    if (paywalled) {
        return (
            <div className="pt-24 md:pt-28 pb-20 mx-auto max-w-5xl px-5">
                <Breadcrumb
                    items={[
                        { name: "Home", to: "/" },
                        { name: "CVE Alerts", to: "/alerts" },
                    ]}
                />
                <PaywallCard />
            </div>
        );
    }

    if (errorMsg) {
        return (
            <div
                className="pt-24 md:pt-28 pb-20 mx-auto max-w-3xl px-5 text-center"
                data-testid="alerts-error"
            >
                <p className="text-text-muted">{errorMsg}</p>
                <Link
                    to="/login"
                    className="inline-block mt-4 text-accent-cyan hover:underline text-sm"
                >
                    Sign in →
                </Link>
            </div>
        );
    }

    return (
        <div
            className="pt-24 md:pt-28 pb-20 mx-auto max-w-7xl px-5 md:px-10"
            data-testid="alerts-page"
        >
            <Breadcrumb
                items={[
                    { name: "Home", to: "/" },
                    { name: "CVE Alerts", to: "/alerts" },
                ]}
            />

            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
                <div>
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan mb-1">
                        Continuous CVE Scanning
                    </p>
                    <h1 className="text-3xl sm:text-4xl font-semibold text-text-primary tracking-tight inline-flex items-center gap-3">
                        <ShieldAlert className="w-7 h-7 text-accent-amber" />
                        CVE Alerts
                    </h1>
                    <p className="text-text-muted text-[14px] mt-2 max-w-xl">
                        Live matching between your hardware inventory and the
                        NIST National Vulnerability Database. Weekly cron + on-
                        demand by the platform admin.
                    </p>
                </div>
                <div className="text-[12px] text-text-dim font-mono text-right shrink-0">
                    <div>
                        Last scan:{" "}
                        <span className="text-text-muted">
                            {fmtRelTime(stats?.last_scan_at)}
                        </span>
                    </div>
                    <div>
                        Next scan:{" "}
                        <span className="text-text-muted">
                            {stats?.next_scan_at
                                ? new Date(stats.next_scan_at).toUTCString().slice(5, 22) + " UTC"
                                : "—"}
                        </span>
                    </div>
                </div>
            </header>

            {/* Filters row */}
            <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-3 sm:p-4 mb-5">
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Filter className="w-3.5 h-3.5 text-text-dim" />
                        <span className="text-[11px] font-mono uppercase tracking-wider text-text-dim mr-2">
                            Status
                        </span>
                        {["new", "acknowledged", "resolved", "suppressed", "all"].map(statusButton)}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-mono uppercase tracking-wider text-text-dim w-[60px]">
                            Severity
                        </span>
                        {["Critical", "High", "Medium", "Low"].map(sevButton)}
                    </div>
                    <div className="flex items-center gap-2">
                        <Search className="w-3.5 h-3.5 text-text-dim" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") load();
                            }}
                            placeholder="CVE-2024-21733, fortinet, hostname, client name…"
                            className="flex-1 bg-canvas/50 border border-white/[0.06] rounded-md px-3 py-1.5 text-[13px] text-text-primary placeholder:text-text-dim focus:border-accent-cyan/50 focus:outline-none"
                            data-testid="alerts-search"
                        />
                        <button
                            type="button"
                            onClick={load}
                            className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.06] bg-surface/40 px-3 py-1.5 text-[12px] text-text-muted hover:text-text-primary hover:border-white/15 transition-colors"
                            data-testid="alerts-refresh"
                        >
                            <RefreshCw
                                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
                            />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Empty / Loading / Table */}
            {loading ? (
                <div className="text-center py-20 text-text-muted">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-accent-cyan" />
                    Loading…
                </div>
            ) : filteredItems.length === 0 ? (
                <div
                    className="text-center py-16 rounded-xl border border-white/[0.06] bg-surface/30"
                    data-testid="alerts-empty"
                >
                    <ShieldAlert className="w-8 h-8 text-accent-mint mx-auto mb-2" />
                    <p className="text-text-muted text-sm">
                        No {statusFilter !== "all" ? statusFilter : ""} CVE
                        alerts match your filters.
                    </p>
                    <p className="text-text-dim text-[12px] mt-1">
                        Last scan: {fmtRelTime(stats?.last_scan_at)}
                    </p>
                    <HowItWorks />
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block rounded-xl border border-white/[0.06] overflow-hidden">
                        <table
                            className="w-full text-[13px]"
                            data-testid="alerts-table"
                        >
                            <thead className="bg-elevated/40">
                                <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-text-dim">
                                    <th className="px-3 py-2 w-[90px]">Severity</th>
                                    <th className="px-3 py-2">CVE</th>
                                    <th className="px-3 py-2 w-[70px]">CVSS</th>
                                    <th className="px-3 py-2">Affected hardware</th>
                                    <th className="px-3 py-2">Client</th>
                                    <th className="px-3 py-2 w-[110px]">Discovered</th>
                                    <th className="px-3 py-2 w-[110px]">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((it) => (
                                    <tr
                                        key={it.id}
                                        className="border-t border-white/[0.04] hover:bg-surface/40 transition-colors"
                                        data-testid={`alert-row-${it.id}`}
                                    >
                                        <td className="px-3 py-2.5">
                                            <SeverityBadge severity={it.cvss_severity || "None"} />
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <Link
                                                to={`/alerts/${it.id}`}
                                                className="text-accent-cyan font-mono hover:underline"
                                            >
                                                {it.cve_id}
                                            </Link>
                                            <p className="text-text-muted text-[12px] mt-0.5 line-clamp-1 max-w-md">
                                                {it.cve_summary}
                                            </p>
                                        </td>
                                        <td className="px-3 py-2.5 font-mono text-text-primary">
                                            {fmtScore(it.cvss_score)}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <div className="text-text-primary">
                                                {it?.hardware?.vendor}{" "}
                                                {it?.hardware?.model}
                                            </div>
                                            {it?.hardware?.hostname && (
                                                <div className="text-text-dim text-[11.5px] font-mono">
                                                    {it.hardware.hostname}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {it?.client?.slug ? (
                                                <Link
                                                    to={`/clients/${it.client.slug}`}
                                                    className="text-text-muted hover:text-accent-cyan"
                                                >
                                                    {it.client.name}
                                                </Link>
                                            ) : (
                                                <span className="text-text-dim">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-text-muted text-[12px]">
                                            {fmtRelTime(it.discovered_at)}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <StatusPill status={it.status} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden grid grid-cols-1 gap-3">
                        {filteredItems.map((it) => (
                            <Link
                                key={it.id}
                                to={`/alerts/${it.id}`}
                                className="rounded-xl border border-white/[0.06] bg-surface/40 p-4 hover:border-white/15 transition-colors"
                                data-testid={`alert-card-${it.id}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <SeverityBadge
                                        severity={it.cvss_severity || "None"}
                                    />
                                    <span className="font-mono text-[13px] text-text-primary">
                                        {fmtScore(it.cvss_score)}
                                    </span>
                                </div>
                                <p className="text-accent-cyan font-mono text-sm">
                                    {it.cve_id}
                                </p>
                                <p className="text-text-muted text-[12.5px] mt-1 line-clamp-2">
                                    {it.cve_summary}
                                </p>
                                <div className="mt-3 flex items-center justify-between text-[11.5px]">
                                    <span className="text-text-dim">
                                        {it?.hardware?.vendor} {it?.hardware?.model}
                                    </span>
                                    <StatusPill status={it.status} />
                                </div>
                            </Link>
                        ))}
                    </div>

                    <HowItWorks />
                </>
            )}
        </div>
    );
}
